import React, { FC, useCallback, useEffect, useState } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import "./EmailTemplateSelector.scss";
import { EmailTemplate } from "../../../../../emailTemplates";

interface Option {
  value: string | undefined;
  label: string;
}

type Props = {
  emailTemplates: EmailTemplate[];
  favoriteTemplate: EmailTemplate | undefined;
  setMessage: (message: string) => void;
};

const EmailTemplateSelector: FC<Props> = ({
  setMessage,
  emailTemplates,
  favoriteTemplate,
}) => {
  const { t } = useTranslation("annotationSideControls");

  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState<
    string | undefined
  >(favoriteTemplate?.id);

  useEffect(() => {
    if (!selectedEmailTemplateId) {
      setMessage("");
      return;
    }

    const emailTemplate = emailTemplates.find(
      (e) => e.id === selectedEmailTemplateId,
    );

    if (!emailTemplate) {
      setMessage("");
      return;
    }

    setMessage(emailTemplate.body);
  }, [selectedEmailTemplateId, emailTemplates, setMessage]);

  // Re-sync the selection when a different favorite arrives. Keyed on the
  // `favoriteTemplate` identity, exactly like the effect this replaces, so the
  // re-sync triggers on the same renders; it just happens before the paint
  // instead of after it. (The old effect also ran on mount, where it could only
  // set the value the state was already initialised to.)
  const [prevFavoriteTemplate, setPrevFavoriteTemplate] =
    useState(favoriteTemplate);
  if (prevFavoriteTemplate !== favoriteTemplate) {
    setPrevFavoriteTemplate(favoriteTemplate);
    setSelectedEmailTemplateId(favoriteTemplate?.id);
  }

  const onChange = useCallback(
    (value: string) => {
      setSelectedEmailTemplateId(value);
    },
    [setSelectedEmailTemplateId],
  );

  const onSearch = useCallback((input: string, option: Option | undefined) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  }, []);

  return (
    <div className="selector-container">
      <h4 className="selector-label">{t("rejectModal.emailTemplate")}</h4>
      <Select
        showSearch
        allowClear
        className="selector"
        value={selectedEmailTemplateId}
        onChange={onChange}
        filterOption={onSearch}
        options={emailTemplates.map((emailTemplate): Option => {
          return { value: emailTemplate.id, label: emailTemplate.name };
        })}
      />
    </div>
  );
};

export default EmailTemplateSelector;
